import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Stack,
  Typography,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableFooter,
} from '@mui/material';
import { Users, PencilSimple, Plus, Buildings } from '@phosphor-icons/react';
import {
  ScreenWrap,
  HeaderBreadcrumbs,
  SoloLoading,
  ScreenError,
} from '../../components';
import { useParams, Link } from 'react-router';
import { useState } from 'react';
import NewTeamModal from './NewTeamModal';
import { Api } from '../../api';
import useModifiableParams from '../../hooks/use-modifiable-params';
import { iriToId, serverImageUrl } from '../../util';
import { format } from 'date-fns';

export default function B2BOrganisationDetailsScreen() {
  const { id } = useParams();

  const { getParams, tablePaginationProps } = useModifiableParams();

  const [teamModalOpen, setTeamModalOpen] = useState(false);
  const [editingTeam, setEditingTeam] = useState<any>(null);

  const params = getParams();

  const orgApi = Api.Organisation.useReadItem(id!);

  const teamsApi = Api.OrganisationTeam.useReadCollectionOrganisationSub(
    id!,
    params,
  );

  const handleNewTeam = () => {
    setEditingTeam(null);
    setTeamModalOpen(true);
  };

  const handleSaveTeam = () => {
    teamsApi?.invalidate();
  };

  const handleCloseTeamModal = () => {
    setTeamModalOpen(false);
    setEditingTeam(null);
  };

  if (orgApi.error) return <ScreenError {...orgApi} />;
  if (teamsApi.error) return <ScreenError {...teamsApi} />;
  if (!orgApi.data) return <SoloLoading />;

  const organisation = orgApi.data;

  return (
    <ScreenWrap>
      <Stack spacing={3}>
        <HeaderBreadcrumbs
          title={organisation.name}
          items={[
            {
              name: 'B2B',
              url: '/b2b',
            },
          ]}
        />

        <Stack direction="row" spacing={3}>
          <Card sx={{ flex: 1 }}>
            <CardHeader
              avatar={
                <Avatar>
                  <Buildings size={24} />
                </Avatar>
              }
              title="Details"
              action={
                <Button
                  variant="outlined"
                  startIcon={<PencilSimple />}
                  component={Link}
                  to={`/b2b/${organisation.id}/edit`}
                >
                  Edit
                </Button>
              }
            />
            <CardContent>
              <Stack spacing={2}>
                <Stack direction="row">
                  <Typography variant="subtitle1" sx={{ width: '50%' }}>
                    Name
                  </Typography>
                  <Typography>{organisation.name}</Typography>
                </Stack>

                <Stack direction="row">
                  <Typography variant="subtitle1" sx={{ width: '50%' }}>
                    Licence Prefix
                  </Typography>
                  <Typography>{organisation.licencePrefix}</Typography>
                </Stack>

                {organisation.logo && (
                  <Stack direction="row" alignItems="center" gap={2}>
                    <Typography variant="subtitle1" sx={{ width: '50%' }}>
                      Company Logo
                    </Typography>
                    <Box
                      component="img"
                      src={serverImageUrl({
                        id: iriToId(organisation.logo),
                        w: 60,
                      })}
                      alt={`${organisation.name} logo`}
                      sx={{
                        width: 60,
                        height: 60,
                        objectFit: 'cover',
                      }}
                    />
                  </Stack>
                )}

                <Stack direction="row">
                  <Typography variant="subtitle1" sx={{ width: '50%' }}>
                    Licences
                  </Typography>
                  <Typography>{organisation.licenceCount}</Typography>
                </Stack>

                <Stack direction="row">
                  <Typography variant="subtitle1" sx={{ width: '50%' }}>
                    Claimed Licences
                  </Typography>
                  <Typography>{organisation.claimedLicenceCount}</Typography>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Stack>

        <Card>
          <CardHeader
            avatar={
              <Avatar>
                <Users size={24} />
              </Avatar>
            }
            title="Teams & Licences"
            action={
              <Button
                variant="outlined"
                startIcon={<Plus />}
                onClick={handleNewTeam}
              >
                New Team
              </Button>
            }
          />
          <CardContent sx={{ pt: 0 }}>
            {teamsApi.data?.member && teamsApi.data.member.length > 0 ? (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Team</TableCell>
                      <TableCell>Coach</TableCell>
                      <TableCell>Program Start Date</TableCell>
                      <TableCell>Licences</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {teamsApi.data.member.map((team) => (
                      <TableRow
                        key={team.id}
                        component={Link}
                        to={`/b2b/${organisation.id}/team/${team.id}`}
                        sx={{
                          cursor: 'pointer',
                          textDecoration: 'none',
                          '&:hover': {
                            backgroundColor: 'action.hover',
                          },
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {team.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {team.coach.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {format(team.programStartDate, 'd MMM yyyy')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {team.licenceCount}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                  <TableFooter>
                    <TablePagination
                      {...tablePaginationProps({
                        totalItems: teamsApi.data?.totalItems ?? 0,
                        rowsPerPage: 10,
                      })}
                      count={teamsApi.data?.totalItems ?? 0}
                    />
                  </TableFooter>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  No teams created yet
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Stack>

      <NewTeamModal
        open={teamModalOpen}
        onClose={handleCloseTeamModal}
        organisationName={organisation.name}
        organisationId={organisation.id}
        existingTeam={editingTeam}
        onSave={handleSaveTeam}
      />
    </ScreenWrap>
  );
}
